import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Expense, ExpenseCategory, FamilyCategory, TravelSubcategory, TruckSubcategory, FuelSubcategory, ExpenseSource, Stats, CATEGORIES } from '../types';
import { useMode } from './ModeContext';
import { useAuth } from './AuthContext';

// Import both cloud API and local storage (for migration/fallback)
import * as cloudApi from '../services/api';
import * as localStorage from '../services/storage';

// Cloud API is now the default - always use it
const USE_CLOUD = true;

interface ExpenseContextType {
  expenses: Expense[];
  stats: Stats | null;
  isLoading: boolean;
  isCloudConnected: boolean;
  addExpense: (data: {
    category: ExpenseCategory | FamilyCategory;
    subcategory?: TravelSubcategory | TruckSubcategory | FuelSubcategory;
    description: string;
    amount?: number;
    date: string;
    file: File | Blob;
    filename: string;
    freightBillNumber?: string;
    vendor?: string;
    vehicleInfo?: string;
    // Duplicate detection fields
    source?: ExpenseSource;
    storeNumber?: string;
    cardLast4?: string;
    transactionId?: string;
  }) => Promise<Expense>;
  deleteExpense: (id: string) => Promise<void>;
  updateExpense: (expense: Expense) => Promise<void>;
  getExpensesByFilter: (filter: {
    category?: ExpenseCategory | FamilyCategory;
    month?: number;
    year?: number;
  }) => Expense[];
  refreshExpenses: () => Promise<void>;
  markBOLAsSent: (id: string) => Promise<void>;
  migrateToCloud: () => Promise<{ migrated: number; errors: number }>;
}

const ExpenseContext = createContext<ExpenseContextType | undefined>(undefined);

export function ExpenseProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { mode } = useMode();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCloudConnected, setIsCloudConnected] = useState(false);

  // Check cloud API health on mount
  useEffect(() => {
    if (USE_CLOUD) {
      cloudApi.checkApiHealth().then(setIsCloudConnected);
    }
  }, []);

  const loadExpenses = useCallback(async () => {
    if (!isAuthenticated && USE_CLOUD) {
      setExpenses([]);
      setStats(null);
      setIsLoading(false);
      return;
    }
    try {
      let loaded: Expense[];
      
      if (USE_CLOUD && isCloudConnected) {
        loaded = await cloudApi.getAllExpenses(undefined, undefined);
        console.log('RECORDS FOUND:', loaded?.length ?? 0, loaded);
      } else {
        loaded = await localStorage.getAllExpenses();
      }
      
      setExpenses(loaded.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ));
      await calculateStats(loaded);
    } catch (error) {
      console.error('Failed to load expenses:', error);
      try {
        const localExpenses = await localStorage.getAllExpenses();
        setExpenses(localExpenses.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ));
        await calculateStats(localExpenses);
      } catch (localError) {
        console.error('Failed to load from local storage:', localError);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isCloudConnected]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const calculateStats = async (expenseList: Expense[]) => {
    let storageInfo: { bytes: number; formatted: string };
    
    if (USE_CLOUD && isCloudConnected) {
      storageInfo = await cloudApi.calculateStorageUsed();
    } else {
      storageInfo = await localStorage.calculateStorageUsed();
    }
    
    const byCategory = CATEGORIES.reduce((acc, cat) => {
      acc[cat.value] = expenseList.filter(e => e.category === cat.value).length;
      return acc;
    }, {} as Record<ExpenseCategory, number>);

    const byMonth: Record<string, number> = {};
    expenseList.forEach(e => {
      const key = `${e.year}-${String(e.month).padStart(2, '0')}`;
      byMonth[key] = (byMonth[key] || 0) + 1;
    });

    const totalAmount = expenseList.reduce((sum, e) => sum + (e.amount || 0), 0);

    let needsReviewCount = 0;
    if (USE_CLOUD && isCloudConnected) {
      try {
        const serverStats = await cloudApi.getStats();
        needsReviewCount = (serverStats as { needsReviewCount?: number }).needsReviewCount ?? 0;
      } catch {
        /* ignore */
      }
    }

    setStats({
      totalExpenses: expenseList.length,
      totalAmount,
      byCategory,
      byMonth,
      storageUsed: storageInfo.bytes,
      storageUsedFormatted: storageInfo.formatted,
      needsReviewCount,
    });
  };

  const fileToBase64 = (file: File | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  const addExpense = async (data: {
    category: ExpenseCategory | FamilyCategory;
    subcategory?: TravelSubcategory | TruckSubcategory | FuelSubcategory;
    description: string;
    amount?: number;
    date: string;
    file: File | Blob;
    filename: string;
    freightBillNumber?: string;
    // Settlement/Trip data
    miles?: number;
    origin?: string;
    destination?: string;
    company?: string;
    // Truck expense tracking
    vendor?: string;
    vehicleInfo?: string;
    // Duplicate detection fields
    source?: ExpenseSource;
    storeNumber?: string;
    cardLast4?: string;
    transactionId?: string;
  }): Promise<Expense> => {
    const dateObj = new Date(data.date);

    let fileUrl: string | undefined;
    let fileType = data.file.type || 'application/octet-stream';
    let fileSize = data.file.size;
    let fileDataForDisplay = '';

    if (USE_CLOUD && isCloudConnected) {
      // Upload binary file to R2 via multipart - NEVER store OCR/parsed text
      const uploadResult = await cloudApi.uploadReceiptFile(data.file, data.filename);
      fileUrl = uploadResult.key;
      fileType = uploadResult.contentType;
      fileSize = uploadResult.size;
      fileDataForDisplay = cloudApi.getFileUrl(uploadResult.key);
    } else {
      // Local storage (IndexedDB): use base64 for fileData
      fileDataForDisplay = await fileToBase64(data.file);
    }

    const expense: Expense = {
      id: uuidv4(),
      category: data.category,
      subcategory: data.subcategory,
      description: data.description,
      amount: data.amount,
      date: data.date,
      month: dateObj.getMonth() + 1,
      year: dateObj.getFullYear(),
      day: dateObj.getDate(),
      filename: data.filename,
      fileData: fileDataForDisplay,
      fileUrl,
      fileType,
      fileSize,
      createdAt: new Date().toISOString(),
      freightBillNumber: data.freightBillNumber,
      isSentToBennett: false,
      // Settlement/Trip data
      miles: data.miles,
      origin: data.origin,
      destination: data.destination,
      company: data.company,
      // Truck expense tracking
      vendor: data.vendor,
      vehicleInfo: data.vehicleInfo,
      // Duplicate detection fields
      source: data.source,
      storeNumber: data.storeNumber,
      cardLast4: data.cardLast4,
      transactionId: data.transactionId,
    };

    const expenseWithMode = { ...expense, mode } as Expense & { mode: string };

    if (USE_CLOUD && isCloudConnected) {
      await cloudApi.addExpense(expenseWithMode);
    } else {
      await localStorage.addExpense(expense);
    }
    
    await loadExpenses();
    return expenseWithMode;
  };

  const deleteExpense = async (id: string) => {
    if (USE_CLOUD && isCloudConnected) {
      await cloudApi.deleteExpense(id);
    } else {
      await localStorage.deleteExpense(id);
    }
    await loadExpenses();
  };

  const updateExpense = async (expense: Expense) => {
    if (USE_CLOUD && isCloudConnected) {
      await cloudApi.updateExpense(expense);
    } else {
      await localStorage.updateExpense(expense);
    }
    await loadExpenses();
  };

  const markBOLAsSent = async (id: string) => {
    const expense = expenses.find(e => e.id === id);
    if (expense) {
      if (USE_CLOUD && isCloudConnected) {
        await cloudApi.markBOLAsSent(id);
      } else {
        await localStorage.updateExpense({ ...expense, isSentToBennett: true });
      }
      await loadExpenses();
    }
  };

  const getExpensesByFilter = (filter: {
    category?: ExpenseCategory;
    month?: number;
    year?: number;
  }): Expense[] => {
    return expenses.filter(e => {
      if (filter.category && e.category !== filter.category) return false;
      if (filter.month && e.month !== filter.month) return false;
      if (filter.year && e.year !== filter.year) return false;
      return true;
    });
  };

  // Migration function: Upload local IndexedDB data to cloud
  const migrateToCloud = async (): Promise<{ migrated: number; errors: number }> => {
    if (!USE_CLOUD) {
      throw new Error('Cloud API not configured. Set VITE_API_BASE_URL in your environment.');
    }

    const isHealthy = await cloudApi.checkApiHealth();
    if (!isHealthy) {
      throw new Error('Cloud API is not responding. Please check your backend deployment.');
    }

    let migrated = 0;
    let errors = 0;

    // Get all local expenses
    const localExpenses = await localStorage.getAllExpenses();
    
    // Upload each expense to cloud
    for (const expense of localExpenses) {
      try {
        await cloudApi.addExpense(expense);
        migrated++;
        console.log(`Migrated expense: ${expense.filename}`);
      } catch (error) {
        console.error(`Failed to migrate expense ${expense.id}:`, error);
        errors++;
      }
    }

    // Migrate settings
    try {
      const localSettings = await localStorage.getSettings();
      await cloudApi.saveSettings(localSettings);
      console.log('Settings migrated successfully');
    } catch (error) {
      console.error('Failed to migrate settings:', error);
    }

    // Refresh from cloud
    setIsCloudConnected(true);
    await loadExpenses();

    return { migrated, errors };
  };

  return (
    <ExpenseContext.Provider
      value={{
        expenses,
        stats,
        isLoading,
        isCloudConnected,
        addExpense,
        deleteExpense,
        updateExpense,
        getExpensesByFilter,
        refreshExpenses: loadExpenses,
        markBOLAsSent,
        migrateToCloud,
      }}
    >
      {children}
    </ExpenseContext.Provider>
  );
}

export function useExpenses() {
  const context = useContext(ExpenseContext);
  if (!context) {
    throw new Error('useExpenses must be used within an ExpenseProvider');
  }
  return context;
}
